import { FC, lazy, Suspense } from 'react';
import type { PickerProps, Theme } from 'emoji-picker-react';

export type { EmojiClickData } from 'emoji-picker-react';

// The picker ships the whole emoji dataset, and it's only needed once someone
// opens it. Everyone imports it from here, so it loads as a separate chunk the
// first time it shows. A lint rule stops anything else importing the package
// directly, which would put it back in the first load.
const loadPicker = () => import('emoji-picker-react');

const Picker = lazy(loadPicker);

// Start loading the picker ahead of time, e.g. when the user hovers the
// button that opens it, so it shows straight away.
export const preloadEmojiPicker = (): void => {
  void loadPicker();
};

type Props = Omit<PickerProps, 'theme'> & {
  // Plain strings, so callers don't need the `Theme` enum.
  theme?: `${Theme}`;
};

// The package's defaults, so the placeholder matches the picker's size.
const DEFAULT_WIDTH = 350;
const DEFAULT_HEIGHT = 450;

export const EmojiPicker: FC<Props> = ({
  theme,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  ...props
}) => (
  <Suspense
    fallback={
      <div
        className="rounded-lg border border-hairline bg-surface-overlay"
        style={{ width, height }}
        aria-busy="true"
      />
    }
  >
    <Picker
      {...props}
      theme={theme as Theme | undefined}
      width={width}
      height={height}
    />
  </Suspense>
);
